/** SAM'S VOICE — the device's own voices only. No service, no key, no cost.
 *
 *  Default is the Australian English voice named "Karen". If she is not
 *  installed: the best other en-AU voice, then the best English voice, with
 *  Enhanced/Premium preferred because they are the ones that do not sound
 *  robotic.
 *
 *  He speaks ONE SENTENCE AT A TIME — several short utterances rather than one
 *  long one — which is what stops the flat machine-gun delivery, and lets the
 *  mouth stop cleanly at the end of each sentence.
 *
 *  Choice, speed and pitch are remembered per user on this device.
 */
const KEY = 'yot_sam_voice';
export const DEFAULTS = { uri: '', rate: 0.95, pitch: 1, speaker: true, keepListening: false };

const synth = () => (typeof window !== 'undefined' ? window.speechSynthesis : null);
export const canSpeak = () => Boolean(synth() && window.SpeechSynthesisUtterance);

const norm = (v) => String((v && v.lang) || '').toLowerCase().replace('_', '-');
const nice = (v) => /enhanced|premium|siri/i.test(String((v && v.name) || ''));

export const listVoices = () => (synth() ? (synth().getVoices() || []) : []);

/** The five groups the gear shows, in this order. */
export const GROUPS = [
    { id: 'en-au', match: (v) => norm(v) === 'en-au' },
    { id: 'en-gb', match: (v) => norm(v) === 'en-gb' },
    { id: 'en', match: (v) => norm(v).indexOf('en') === 0 && norm(v) !== 'en-au' && norm(v) !== 'en-gb' },
    { id: 'fr', match: (v) => norm(v).indexOf('fr') === 0 },
    { id: 'es', match: (v) => norm(v).indexOf('es') === 0 },
];

export const grouped = (voices) => GROUPS.map((g) => ({
    id: g.id, voices: voices.filter(g.match),
}));

/** KAREN FIRST. */
export const pickDefault = (voices) => {
    const au = voices.filter((v) => norm(v) === 'en-au');
    const karen = au.find((v) => /\bkaren\b/i.test(v.name || ''));
    if (karen) return karen;
    const bestAu = au.find(nice) || au[0];
    if (bestAu) return bestAu;
    const en = voices.filter((v) => norm(v).indexOf('en') === 0);
    return en.find(nice) || en[0] || voices[0] || null;
};

export const readPrefs = (uid) => {
    try {
        const raw = JSON.parse(window.localStorage.getItem(`${KEY}:${uid || 'anon'}`) || '{}');
        return { ...DEFAULTS, ...raw };
    } catch (_e) { return { ...DEFAULTS }; }
};

export const writePrefs = (uid, prefs) => {
    try {
        window.localStorage.setItem(`${KEY}:${uid || 'anon'}`, JSON.stringify(prefs));
        // The slim bar and the panel both own these buttons, so whoever changes
        // one tells the other.
        window.dispatchEvent(new CustomEvent('sam-prefs'));
    } catch (_e) { /* a private window simply does not remember */ }
};

export const stop = () => { try { synth().cancel(); } catch (_e) { /* nothing to cancel */ } };

/** Split into sentences. The first two are spoken; the rest waits behind
 *  "Read the rest" so a long answer never traps somebody in a monologue. */
export const sentences = (text) => String(text || '')
    .match(/[^.!?]+[.!?]*/g)?.map((s) => s.trim()).filter(Boolean) || [];

/**
 *  speak(text, { voice, rate, pitch, lang, onStart, onBoundary, onEnd })
 *  Returns false when the device cannot speak, so the caller still shows text.
 */
export const speak = (text, opts = {}) => {
    const lines = sentences(text);
    if (!canSpeak() || !lines.length) return false;
    stop();
    const { voice, rate = DEFAULTS.rate, pitch = DEFAULTS.pitch, lang = 'en' } = opts;
    const fallbackLang = lang === 'fr' ? 'fr-CA' : (lang === 'es' ? 'es-ES' : 'en-CA');
    lines.forEach((line, i) => {
        const u = new window.SpeechSynthesisUtterance(line);
        u.lang = voice ? voice.lang : fallbackLang;
        if (voice) u.voice = voice;
        u.rate = rate;
        u.pitch = pitch;
        if (i === 0) u.onstart = () => opts.onStart?.();
        u.onboundary = () => opts.onBoundary?.();
        if (i === lines.length - 1) u.onend = () => opts.onEnd?.();
        u.onerror = () => { if (i === lines.length - 1) opts.onEnd?.(); };
        synth().speak(u);
    });
    return true;
};
